/**
 * sha256 of asset bytes, computed on device BEFORE the client ever asks for
 * an upload URL. This is what makes `POST /capture/upload-url`'s
 * `duplicateOf` response possible: the fingerprint has to exist before the
 * request goes out, not be derived from it. See src/lib/api/capture.ts
 * `uploadCaptureAsset`, which never uploads bytes for a fingerprint the
 * server already has.
 */
export async function sha256Fingerprint(bytes: Blob): Promise<string> {
  const buffer = await bytes.arrayBuffer();
  // Pass a view, not the raw ArrayBuffer. Under jsdom on node 20 the buffer
  // returned by Blob.arrayBuffer() belongs to a different realm and
  // SubtleCrypto rejects it as "not instance of ArrayBuffer".
  const digest = await crypto.subtle.digest('SHA-256', new Uint8Array(buffer));
  return toHex(digest);
}

/**
 * The capture's own fingerprint, over the ordered asset fingerprints.
 *
 * The server uses a capture's fingerprint as the idempotency key for
 * `POST /capture`, and it uses each asset's fingerprint separately for the
 * presign and the duplicate gate. So a capture of three angles has four
 * fingerprints, and this is the one that says "these three frames, in this
 * order, are one capture".
 *
 * Order is part of the identity. The same three frames sent in a different
 * order are a different capture, because the first asset is the one the
 * review console shows as the thumbnail.
 *
 * A single-asset capture still hashes its one fingerprint rather than
 * reusing it, so every capture's key is computed the same way whatever it
 * carries.
 */
export async function captureFingerprint(assetFingerprints: string[]): Promise<string> {
  const joined = new TextEncoder().encode(assetFingerprints.join('\n'));
  const digest = await crypto.subtle.digest('SHA-256', joined);
  return toHex(digest);
}

function toHex(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let hex = '';
  for (const b of bytes) {
    hex += b.toString(16).padStart(2, '0');
  }
  return hex;
}
